import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import {mainQuery} from '@github-ui/react-core/future/main-query'
import {
  type RepoPullsDashboardContentResponse,
  RepoPullsDashboardContentResponseSchema,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/repo_pulls_dashboard'

import {pullRequestsAppBuilder} from '../config/app-builder'
import {
  createRepoPullsProtoPersister,
  type LegacyRepoPullsDashboardContentPayload,
  normalizeLegacyRepoPullsContent,
  REPO_PULLS_CONTENT_PERSISTER_CONFIG,
} from '../helpers/repo-pulls-dashboard-proto'

export const REPO_PULLS_DEFAULT_QUERY = 'is:pr state:open'

export const REPO_PULLS_CLEAN_PATHS = [
  {
    path: '/:owner/:repo/pulls/created_by/:creator',
    pattern: /^created_by\/([^/]+)$/,
    param: 'creator',
    qualifier: 'author',
  },
  {
    path: '/:owner/:repo/pulls/assigned/:assignee',
    pattern: /^assigned\/([^/]+)$/,
    param: 'assignee',
    qualifier: 'assignee',
  },
  {
    path: '/:owner/:repo/pulls/mentioned/:mentioned',
    pattern: /^mentioned\/([^/]+)$/,
    param: 'mentioned',
    qualifier: 'mentions',
  },
  {
    path: '/:owner/:repo/pulls/review-requested/:review_requested',
    pattern: /^review-requested\/([^/]+)$/,
    param: 'review_requested',
    qualifier: 'review-requested',
  },
  {path: '/:owner/:repo/pulls/:login', pattern: /^([^/]+)$/, param: 'login', qualifier: 'author'},
] as const

function cleanPathQualifier(pathname: string): string | undefined {
  const cleanPath = pathname.split('/pulls/')[1]
  if (!cleanPath) return undefined

  for (const {pattern, qualifier} of REPO_PULLS_CLEAN_PATHS) {
    const value = pattern.exec(cleanPath)?.[1]
    if (value) return `${qualifier}:${decodeURIComponent(value)}`
  }

  return undefined
}

type RepoPullsCleanPathParam = (typeof REPO_PULLS_CLEAN_PATHS)[number]['param']
type RepoPullsCleanPathParams = Partial<Record<RepoPullsCleanPathParam, string>>

function cleanPathQualifierFromParams(params?: RepoPullsCleanPathParams): string | undefined {
  if (!params) return undefined

  for (const {param, qualifier} of REPO_PULLS_CLEAN_PATHS) {
    const value = params[param]
    if (value) return `${qualifier}:${value}`
  }

  return undefined
}

function appendQualifier(query: string, qualifier: string | undefined) {
  return qualifier ? `${query} ${qualifier}` : query
}

export function repoPullsQueryResolver({
  pathname,
  searchParams,
}: {
  pathname: string
  searchParams: URLSearchParams
}): string {
  const query = searchParams.get('q')?.trim() || REPO_PULLS_DEFAULT_QUERY
  return appendQualifier(query, cleanPathQualifier(pathname))
}

function repoPullsSearchParams(searchParams: URLSearchParams, qualifier?: string) {
  const params: Record<string, string> = {
    q: appendQualifier(searchParams.get('q')?.trim() || REPO_PULLS_DEFAULT_QUERY, qualifier),
    page: searchParams.get('page') || '1',
  }
  const milestone = searchParams.get('milestone')
  if (milestone) params.milestone = milestone
  return params
}

export function buildRepoPullsDashboardContentRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  return appBuilder.createQueryRouteConfig('repoPullsDashboardContentRoute', {
    path: '/:owner/:repo/pulls',
    queries: [
      mainQuery<RepoPullsDashboardContentResponse>({
        schema: RepoPullsDashboardContentResponseSchema,
        adapt: legacy => normalizeLegacyRepoPullsContent(legacy as LegacyRepoPullsDashboardContentPayload),
        persister: createRepoPullsProtoPersister(
          RepoPullsDashboardContentResponseSchema,
          'repoPullsDashboardContentRoute',
          REPO_PULLS_CONTENT_PERSISTER_CONFIG,
        ),
        queryDeps: ({pathname, params, searchParams}) => ({
          pathname,
          searchParams: repoPullsSearchParams(
            searchParams,
            cleanPathQualifierFromParams(params as RepoPullsCleanPathParams),
          ),
        }),
      }),
    ],
  })
}

export const repoPullsDashboardContentRoute = buildRepoPullsDashboardContentRoute(pullRequestsAppBuilder)
